"use client";

import React from "react";
import { Tooltip } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import StarIcon from "@mui/icons-material/Star";

export type AccessLevel = "primary" | "secondary";

export interface PassAccessInfo {
  passName: string;
  accessLevel: AccessLevel;
}

interface PassAccessBadgeProps {
  /** List of passes that grant access to this event */
  passes: PassAccessInfo[];
  /** Whether to show compact view (icons only) or full view (with labels) */
  compact?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Displays badges showing which passes grant access to an event.
 * Primary access = full access (marked with star)
 * Secondary access = discovery access (marked with lock icon)
 */
export default function PassAccessBadge({
  passes,
  compact = false,
  className = "",
}: PassAccessBadgeProps) {
  if (!passes || passes.length === 0) {
    return null;
  }

  // Group passes by access level
  const primaryPasses = passes.filter((p) => p.accessLevel === "primary");
  const secondaryPasses = passes.filter((p) => p.accessLevel === "secondary");

  if (compact) {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        {primaryPasses.length > 0 && (
          <Tooltip
            title={`Full access: ${primaryPasses.map((p) => p.passName).join(", ")}`}
          >
            <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">
              <StarIcon fontSize="inherit" />
              <span>{primaryPasses.length}</span>
            </div>
          </Tooltip>
        )}
        {secondaryPasses.length > 0 && (
          <Tooltip
            title={`Discovery access: ${secondaryPasses.map((p) => p.passName).join(", ")}`}
          >
            <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
              <LockOpenIcon fontSize="inherit" />
              <span>{secondaryPasses.length}</span>
            </div>
          </Tooltip>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {primaryPasses.map((pass) => (
        <Tooltip key={pass.passName} title="Full access to this event">
          <div className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">
            <StarIcon fontSize="inherit" />
            <span>{pass.passName}</span>
          </div>
        </Tooltip>
      ))}
      {secondaryPasses.map((pass) => (
        <Tooltip key={pass.passName} title="Discovery access (general admission)">
          <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs">
            <LockOpenIcon fontSize="inherit" />
            <span>{pass.passName}</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}
